import { isEqualWith } from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { store } from '../store';
import { base36encode, randomAlphanumeric, wait } from '../utils';
import { type QSheetsMetaRecord, db } from './db';

interface MetaFileData {
  active?: string;
  meta_uuid?: string;
  last_saved_at?: number;
  last_modified_at?: number;
  [tabId: string]: unknown;
}

interface TabMetaData {
  order_by?: number;
  name?: string;
  [key: string]: unknown;
}

interface EditorState {
  qSheetList: string[];
}

interface RootState {
  editor: EditorState;
}

interface NewTabResult {
  name: string;
  cluster: null;
  database: null;
  order_by: number;
  group_uuid: string;
  ref_id: string | null;
}

const customIsEqual = (obj1: unknown, obj2: unknown, keysToIgnore: string[] = []): boolean => {
  return isEqualWith(obj1, obj2, (_val1, _val2, key) => {
    if (keysToIgnore.includes(key as string)) {
      return true; // Skip comparison for these keys
    }
    return undefined; // Use default comparison for other keys
  });
};

const sortMetaData = (metaFileData: MetaFileData): MetaFileData => {
  const activeEntry: Pick<
    MetaFileData,
    'active' | 'meta_uuid' | 'last_saved_at' | 'last_modified_at'
  > = {
    active: metaFileData?.['active'],
    meta_uuid: metaFileData?.['meta_uuid'],
    last_saved_at: metaFileData?.['last_saved_at'] ?? 1,
    last_modified_at: metaFileData?.['last_modified_at'] ?? 2,
  };

  const dataCopy = { ...metaFileData };
  delete dataCopy['active'];
  delete dataCopy['last_modified_at'];
  delete dataCopy['last_saved_at'];
  delete dataCopy['meta_uuid'];

  const sortedEntries = Object.entries(dataCopy).sort(([, valueA], [, valueB]) => {
    const orderA =
      (valueA as TabMetaData)?.order_by !== undefined
        ? (valueA as TabMetaData).order_by!
        : Infinity;
    const orderB =
      (valueB as TabMetaData)?.order_by !== undefined
        ? (valueB as TabMetaData).order_by!
        : Infinity;
    return orderA - orderB;
  });

  const sortedData: MetaFileData = Object.assign(
    {},
    activeEntry,
    Object.fromEntries(sortedEntries)
  );

  return sortedData;
};

export const upsertQSheetsMeta = async (
  metaData: MetaFileData,
  recordTimestamp: boolean = true
): Promise<void | null> => {
  try {
    await wait(500); // wait for redux to update for safe side
    metaData = sortMetaData(metaData);
    if (recordTimestamp) {
      metaData['last_modified_at'] = moment().unix();
    }

    const record: QSheetsMetaRecord = { from: 'editor', meta: metaData };

    if (!record?.meta?.active) {
      console.log('QUERY_SHEET_META : activeKey is missing in upsertQSheetsMeta : ', record);
      return null;
    }

    const existingRecord = await getQSheetsMeta();
    if (existingRecord) {
      const areEqual = customIsEqual(existingRecord, record, [
        'last_modified_at',
        'last_saved_at',
        'meta_uuid',
      ]);
      if (!areEqual) {
        console.log(
          'META_CACHE : last_saved_at : ',
          moment.unix(recordTimestamp ? moment().unix() : 0).format('YYYY-MM-DD HH:mm:ss')
        );
        await db.qSheetsMeta
          .where('from')
          .equals('editor')
          .modify((item) => {
            item.meta = record.meta;
          });
      }
    } else {
      await db.qSheetsMeta.add(record); // If it doesn't exist, insert a new record
      console.log(`META_CACHE : meta init for : editor`);
    }
  } catch (error) {
    const msg = `Failed to upsert the qSheetsMeta : ${error}`;
    throw new Error(msg);
  }
};

export async function getQSheetsMeta(): Promise<QSheetsMetaRecord | undefined> {
  try {
    return await db.qSheetsMeta.where('from').equals('editor').first();
  } catch (error) {
    console.error('Failed to fetch meta :', error);
    throw new Error('Failed to fetch meta');
  }
}

export async function createNewTabInMeta(
  tabName: string,
  order_by: number,
  duplicate: boolean = false
): Promise<NewTabResult> {
  try {
    await wait(300); // wait for redux to update for safe side
    const tab_id =
      base36encode(Math.floor(Date.now() / 1000)).toLowerCase() + randomAlphanumeric(4);

    await db.qSheetsMeta
      .where('from')
      .equals('editor')
      .modify((item) => {
        item.meta['active'] = tab_id;
        item.meta['last_modified_at'] = moment().unix();
        item.meta[tab_id] = {
          name: tabName,
          order_by,
        };
      });
    return {
      name: tabName,
      cluster: null,
      database: null,
      order_by,
      group_uuid: tab_id,
      ref_id: duplicate ? null : uuidv4(),
    };
  } catch (error) {
    console.error('Failed to create record in meta :', error);
    throw new Error('Failed to create record in meta');
  }
}

export async function updateLastSaveInMeta(savedAt: number): Promise<boolean | null> {
  if (!savedAt) {
    return null;
  }

  try {
    await wait(500); // wait for redux to update for safe side

    await db.qSheetsMeta
      .where('from')
      .equals('editor')
      .modify((item) => {
        item.meta['last_saved_at'] = savedAt;
      });
    return true;
  } catch (error) {
    console.error('Failed to update sheet with saved_at in meta :', error);
    throw new Error('Failed to update sheet with saved_at in meta');
  }
}

export async function updateActiveSheetInMeta(tab_id: string): Promise<boolean | null> {
  if (!tab_id) {
    return null;
  }
  try {
    await db.qSheetsMeta
      .where('from')
      .equals('editor')
      .modify((item) => {
        item.meta['active'] = tab_id;
        item.meta['last_modified_at'] = moment().unix();
      });
    return true;
  } catch (error) {
    console.error('Failed to update active sheet in meta :', error);
    throw new Error('Failed to update active sheet in meta');
  }
}

export async function updateTabNameInMeta(tab_id: string, newName: string): Promise<boolean> {
  try {
    await db.qSheetsMeta
      .where('from')
      .equals('editor')
      .modify((item) => {
        const tabData = item.meta[tab_id] as TabMetaData;
        if (tabData) {
          tabData.name = newName;
        }
        item.meta['last_modified_at'] = moment().unix();
      });
    return true;
  } catch (error) {
    console.error('Failed to update sheet name in meta :', error);
    throw new Error('Failed to update sheet name in meta');
  }
}

export async function updateTabSequenceInMeta(): Promise<boolean> {
  try {
    const existingMeta = await getQSheetsMeta();
    const old_meta = existingMeta?.meta || {};
    const state = store.getState() as RootState;
    const querySheetList = state?.editor?.qSheetList ?? [];
    querySheetList?.forEach((item, tab_index) => {
      const tabData = old_meta[item] as TabMetaData | undefined;
      if (tabData && 'order_by' in tabData) {
        tabData['order_by'] = tab_index;
      }
    });

    old_meta['last_modified_at'] = moment().unix();

    await db.qSheetsMeta
      .where('from')
      .equals('editor')
      .modify((item) => {
        item.meta = old_meta;
      });

    return true;
  } catch (error) {
    console.error('Failed to update TabSequenceInMeta  in meta :', error);
    throw new Error('Failed to update TabSequenceInMeta  in meta');
  }
}

export async function deleteTabFromMeta(tabId: string): Promise<boolean | null> {
  if (!tabId) {
    return null;
  }
  try {
    const existingMeta = await getQSheetsMeta();
    const meta = existingMeta?.meta || {};
    const exist_meta_uuid = meta?.['meta_uuid'];
    const exist_last_saved_at = meta?.['last_saved_at'];

    const sortedMeta = sortMetaData(meta);
    let sortedMetaKeys = Object.keys(sortedMeta);
    sortedMetaKeys = sortedMetaKeys.filter(
      (item) => !['active', 'last_saved_at', 'last_modified_at', 'meta_uuid'].includes(item)
    );
    if (sortedMetaKeys.length > 1) {
      const current_active_index = sortedMetaKeys.indexOf(tabId);
      let activatingIndex = current_active_index === 0 ? 1 : current_active_index - 1;

      if (activatingIndex === -1) {
        activatingIndex = 0;
      }

      console.log('QUERY_SHEET_META : deleting sheet :', {
        sheet_list: sortedMetaKeys,
        deleting_sheet_index: current_active_index,
        next_activating_index: activatingIndex,
        next_active_uuid: sortedMetaKeys[activatingIndex],
      });

      meta['active'] = sortedMetaKeys[activatingIndex];
      meta['meta_uuid'] = exist_meta_uuid;
      meta['last_saved_at'] = exist_last_saved_at;
      delete meta?.[tabId];
      await upsertQSheetsMeta(meta);
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to delete tab from meta :', error);
    throw new Error('Failed to delete tab from meta');
  }
}

export const clearQSheetsMetaTable = async (tableName?: string): Promise<void> => {
  try {
    await db.qSheetsMeta.clear();
  } catch (error) {
    console.error(`IndexedDB: Error clearing table ${tableName}:`, error);
    throw new Error('Failed to clear table');
  }
};
